import { useMemo } from 'react';
import type { PlatformEvent } from '../../types/events';

export type GroupMode = 'none' | 'run' | 'source';

export interface SingleEventItem {
  kind: 'single';
  event: PlatformEvent;
}

export interface SubAgentGroup {
  kind: 'subagent-group';
  runId: string;
  agentName?: string;
  events: PlatformEvent[];
  latestTimestamp: string;
  status: 'running' | 'completed' | 'failed' | 'cancelled';
}

export function isDeltaEvent(eventType: string): boolean {
  return eventType.endsWith('_delta');
}

/**
 * Returns the run-group key for a PlatformEvent, or null if it's not a
 * run-grouped event. Sub-agent events fold into the coordinator's run via
 * `parentRunId`; standalone run events use `subjectId`. Mirrors `groupByRun`.
 */
export function getRunGroupKey(event: PlatformEvent): string | null {
  if (!event.eventType.startsWith('agent.run.') || !event.subjectId) return null;
  return (event.data?.parentRunId as string) || event.subjectId;
}

export function getLatestNonDeltaEvent(events: PlatformEvent[]): PlatformEvent | undefined {
  // events are sorted newest-first
  return events.find((e) => !isDeltaEvent(e.eventType));
}

export interface RunGroupItem {
  kind: 'group';
  subjectId: string;
  agentName?: string;
  events: PlatformEvent[];
  parentEvents: PlatformEvent[];
  subAgentGroups: SubAgentGroup[];
  timelineItems: Array<PlatformEvent | SubAgentGroup>;
  latestTimestamp: string;
  status: 'running' | 'completed' | 'failed' | 'cancelled';
}

export interface SourceGroupItem {
  kind: 'source-group';
  source: string;
  events: PlatformEvent[];
  latestTimestamp: string;
}

export type EventListItem = SingleEventItem | RunGroupItem | SourceGroupItem;

function matchesExcludePattern(eventType: string, pattern: string): boolean {
  if (pattern.endsWith('.')) {
    return eventType.startsWith(pattern);
  }
  return eventType === pattern;
}

function deriveRunStatus(events: PlatformEvent[]): 'running' | 'completed' | 'failed' | 'cancelled' {
  for (const e of events) {
    if (e.eventType === 'agent.run.failed') return 'failed';
  }
  for (const e of events) {
    if (e.eventType === 'agent.run.cancelled') return 'cancelled';
  }
  for (const e of events) {
    if (e.eventType === 'agent.run.completed') return 'completed';
  }
  return 'running';
}

function sortEventsNewestFirst(events: PlatformEvent[]): void {
  events.sort(
    (a, b) =>
      new Date(b.metadata.timestamp).getTime() -
      new Date(a.metadata.timestamp).getTime()
  );
}

function deriveSubAgentStatus(events: PlatformEvent[]): 'running' | 'completed' | 'failed' | 'cancelled' {
  for (const e of events) {
    if (e.eventType === 'agent.run.subagent_completed') {
      const data = e.data as { success?: boolean };
      if (data.success === false) return 'failed';
      return 'completed';
    }
    if (e.eventType === 'agent.run.failed') return 'failed';
    if (e.eventType === 'agent.run.cancelled') return 'cancelled';
    if (e.eventType === 'agent.run.completed') return 'completed';
  }
  return 'running';
}

function getEarliestTimestamp(events: PlatformEvent[]): string {
  let earliest = events[0].metadata.timestamp;
  for (let i = 1; i < events.length; i++) {
    if (events[i].metadata.timestamp < earliest) {
      earliest = events[i].metadata.timestamp;
    }
  }
  return earliest;
}

function buildTimelineItems(
  parentEvents: PlatformEvent[],
  subAgentGroups: SubAgentGroup[]
): Array<PlatformEvent | SubAgentGroup> {
  // Parent events are newest-first; reverse to oldest-first for timeline
  const items: Array<{ ts: string; item: PlatformEvent | SubAgentGroup }> = [];

  for (const e of parentEvents) {
    items.push({ ts: e.metadata.timestamp, item: e });
  }
  for (const g of subAgentGroups) {
    // Position sub-agent group by its earliest event timestamp
    items.push({ ts: getEarliestTimestamp(g.events), item: g });
  }

  // Sort newest-first (latest events at the top)
  items.sort((a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime());
  return items.map((i) => i.item);
}

function groupByRun(filtered: PlatformEvent[]): EventListItem[] {
  const runGroups = new Map<string, PlatformEvent[]>();
  const singles: SingleEventItem[] = [];

  for (const event of filtered) {
    const groupKey = getRunGroupKey(event);
    if (groupKey) {
      const group = runGroups.get(groupKey);
      if (group) {
        group.push(event);
      } else {
        runGroups.set(groupKey, [event]);
      }
    } else {
      singles.push({ kind: 'single', event });
    }
  }

  const groupItems: RunGroupItem[] = [];
  for (const [subjectId, groupEvents] of runGroups) {
    sortEventsNewestFirst(groupEvents);

    // Partition into parent vs sub-agent events
    const parentEvents: PlatformEvent[] = [];
    const subAgentEventMap = new Map<string, PlatformEvent[]>();

    for (const e of groupEvents) {
      const parentRunId = e.data?.parentRunId as string | undefined;
      if (parentRunId) {
        // Sub-agent event — group by the sub-agent's own runId
        const childRunId = (e.data?.runId as string) || e.subjectId || 'unknown';
        const bucket = subAgentEventMap.get(childRunId);
        if (bucket) {
          bucket.push(e);
        } else {
          subAgentEventMap.set(childRunId, [e]);
        }
      } else {
        parentEvents.push(e);
      }
    }

    // Build SubAgentGroup objects
    const subAgentGroups: SubAgentGroup[] = [];
    for (const [runId, saEvents] of subAgentEventMap) {
      sortEventsNewestFirst(saEvents);
      const agentName = saEvents.find((e) => e.data?.agentName)?.data?.agentName as
        | string
        | undefined;
      subAgentGroups.push({
        kind: 'subagent-group',
        runId,
        agentName,
        events: saEvents,
        latestTimestamp: saEvents[0].metadata.timestamp,
        status: deriveSubAgentStatus(saEvents),
      });
    }

    const agentName = parentEvents.find((e) => e.data?.agentName)?.data?.agentName as
      | string
      | undefined;

    groupItems.push({
      kind: 'group',
      subjectId,
      agentName,
      events: groupEvents,
      parentEvents,
      subAgentGroups,
      timelineItems: buildTimelineItems(parentEvents, subAgentGroups),
      latestTimestamp: groupEvents[0].metadata.timestamp,
      status: deriveRunStatus(groupEvents),
    });
  }

  return [...groupItems, ...singles];
}

function groupBySource(filtered: PlatformEvent[]): EventListItem[] {
  const sourceGroups = new Map<string, PlatformEvent[]>();

  for (const event of filtered) {
    const src = event.source || 'unknown';
    const group = sourceGroups.get(src);
    if (group) {
      group.push(event);
    } else {
      sourceGroups.set(src, [event]);
    }
  }

  const items: SourceGroupItem[] = [];
  for (const [source, groupEvents] of sourceGroups) {
    sortEventsNewestFirst(groupEvents);
    items.push({
      kind: 'source-group',
      source,
      events: groupEvents,
      latestTimestamp: groupEvents[0].metadata.timestamp,
    });
  }

  return items;
}

function getItemTimestamp(item: EventListItem): string {
  if (item.kind === 'single') return item.event.metadata.timestamp;
  return item.latestTimestamp;
}

export function useEventGrouping(
  events: PlatformEvent[],
  options: { groupMode: GroupMode; excludedTypes: Set<string> }
): EventListItem[] {
  const { groupMode, excludedTypes } = options;

  return useMemo(() => {
    // Step 1: Filter out excluded event types
    const filtered = events.filter((e) => {
      for (const pattern of excludedTypes) {
        if (matchesExcludePattern(e.eventType, pattern)) return false;
      }
      return true;
    });

    // Step 2: Group (or not)
    let items: EventListItem[];

    if (groupMode === 'run') {
      items = groupByRun(filtered);
    } else if (groupMode === 'source') {
      items = groupBySource(filtered);
    } else {
      items = filtered.map<SingleEventItem>((event) => ({ kind: 'single', event }));
    }

    // Step 3: Sort by latest timestamp (newest first)
    items.sort((a, b) => {
      return new Date(getItemTimestamp(b)).getTime() - new Date(getItemTimestamp(a)).getTime();
    });

    return items;
  }, [events, groupMode, excludedTypes]);
}
